import { useState } from "react";
import { api, ApiError, type RunPipelineResultResponse } from "../../api/client";
import { Icon } from "../../shell/Icon";

const PHASE_OPTIONS: Array<{ value: number | undefined; label: string; hint: string }> = [
  {
    value: 3,
    label: "Bis Phase 3",
    hint: "Deterministisch — kein API-Key nötig",
  },
  {
    value: 4,
    label: "Bis Phase 4",
    hint: "Inklusive Sonnet-Match",
  },
  {
    value: 5,
    label: "Bis Phase 5",
    hint: "+ Bepreisung",
  },
  {
    value: undefined,
    label: "Voll bis Phase 6",
    hint: "+ Excel/PDF/Anschreiben",
  },
];

export function RunDialog({
  tenantId,
  anfrageId,
  onClose,
  onSuccess,
}: {
  tenantId: string;
  anfrageId: string;
  onClose: () => void;
  onSuccess: (result: RunPipelineResultResponse) => void;
}) {
  const [untilPhaseIdx, setUntilPhaseIdx] = useState(3); // default: voll
  const [autoPick, setAutoPick] = useState(true);
  // Default an: schützt manuelle Mengen-Edits. Bei resume=aus rechnet die
  // Pfad-B-Engine Phase 2 neu und überschreibt im MengenTab korrigierte
  // Räume/Flächen. Resume=an überspringt die vorhandenen Phasen 1+2 und
  // erzeugt nur die nach einem Edit invalidierten Folge-Phasen (3–6, 98) neu.
  const [resume, setResume] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunPipelineResultResponse | null>(null);

  async function handleRun() {
    setPending(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.runPipeline(tenantId, anfrageId, {
        untilPhase: PHASE_OPTIONS[untilPhaseIdx]!.value,
        autoPickMatches: autoPick,
        resume,
      });
      setResult(r);
      onSuccess(r);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setError(body?.hint ?? body?.error ?? err.message);
      } else {
        setError(String(err));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 720 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="uppercase-mini">// PIPELINE</div>
            <div className="modal-title">Pipeline starten</div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        {!result && (
          <>
            <div className="modal-body">
              <div className="uppercase-mini" style={{ marginBottom: 10 }}>
                // BIS WELCHE PHASE
              </div>
              <div className="run-modes">
                {PHASE_OPTIONS.map((opt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`run-mode ${untilPhaseIdx === idx ? "on" : ""}`}
                    onClick={() => setUntilPhaseIdx(idx)}
                    disabled={pending}
                  >
                    <div className="run-mode-h">{opt.label}</div>
                    <div className="run-mode-p">{opt.hint}</div>
                  </button>
                ))}
              </div>

              <div className="cfg-toggle-list" style={{ marginTop: 8 }}>
                <label className="toggle-row" style={{ cursor: "pointer" }}>
                  <div>
                    <div className="toggle-row-l">Auto-Pick für auto_uebernehmen-Treffer</div>
                    <div className="toggle-row-h">
                      Nimmt vor Phase 5 automatisch den ersten Treffer mit Score ≥ 80.
                      Überschreibt manuelle Auswahl nicht.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={autoPick}
                    onChange={(e) => setAutoPick(e.currentTarget.checked)}
                    disabled={pending}
                  />
                </label>
                <label className="toggle-row" style={{ cursor: "pointer" }}>
                  <div>
                    <div className="toggle-row-l">Resume (vorhandene Phasen behalten)</div>
                    <div className="toggle-row-h">
                      <strong>An (empfohlen):</strong> behält die manuell editierten
                      Mengen (Phase 2) und rechnet nur die nach einem Mengen-Edit
                      ungültigen Folge-Phasen (3–6, QS) neu. <strong>Aus:</strong>
                      kompletter Neulauf — die Pfad-B-Engine extrahiert die Mengen
                      neu und überschreibt dabei deine Raum-/Flächen-Korrekturen.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={resume}
                    onChange={(e) => setResume(e.currentTarget.checked)}
                    disabled={pending}
                  />
                </label>
              </div>

              {error && (
                <div className="run-warn" style={{ marginTop: 16 }}>
                  <span className="warn-dot" />
                  <span>
                    <strong>Pipeline fehlgeschlagen:</strong> {error}
                  </span>
                </div>
              )}
            </div>

            <div className="modal-foot" style={{ justifyContent: "flex-end" }}>
              <button type="button" className="btn ghost" onClick={onClose} disabled={pending}>
                Abbrechen
              </button>
              <button
                type="button"
                className={`btn primary ${pending ? "is-running" : ""}`}
                onClick={handleRun}
                disabled={pending}
              >
                {pending ? (
                  <>
                    <span className="spinner" /> Läuft … (~60s)
                  </>
                ) : (
                  <>
                    <Icon name="play" size={14} /> Starten
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {result && <RunResultView result={result} onClose={onClose} />}
      </div>
    </div>
  );
}

function RunResultView({
  result,
  onClose,
}: {
  result: RunPipelineResultResponse;
  onClose: () => void;
}) {
  const phaseFailed = result.summaries.some((s) => s.status === "failed");
  return (
    <>
      <div className="modal-body">
        <section
          className="block"
          style={{
            marginBottom: 16,
            borderColor: phaseFailed
              ? "oklch(0.72 0.18 25 / 0.4)"
              : "oklch(0.78 0.16 152 / 0.4)",
          }}
        >
          <div className="block-head">
            <span
              className="uppercase-mini"
              style={{ color: phaseFailed ? "var(--bad)" : "var(--ok)" }}
            >
              // PIPELINE ABGESCHLOSSEN · PFAD {result.pfad ?? "?"}
            </span>
          </div>
          <div className="notice-body">
            <div>
              <span className="mono">{result.summaries.length}</span> Phasen,{" "}
              <span className="mono">{result.klaerpunkteOffen}</span> offene Klärpunkte
              {phaseFailed && (
                <span style={{ color: "var(--bad)", marginLeft: 8 }}>(mit Fehlern)</span>
              )}
            </div>
          </div>
        </section>
        <div style={{ display: "grid", gap: 4 }}>
          {result.summaries.map((s) => (
            <div
              key={`${s.phase}-${s.slug}`}
              className="run-phase-row"
              style={{ cursor: "default" }}
            >
              <span className="run-phase-num mono">
                {String(s.phase).padStart(2, "0")}
              </span>
              <span>{s.slug}</span>
              <span className={`run-phase-state ${s.status === "ok" ? "is-run" : ""}`}>
                {s.status} · {(s.durationMs / 1000).toFixed(2)}s
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="modal-foot" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="btn primary" onClick={onClose}>
          Schliessen
        </button>
      </div>
    </>
  );
}
